import {
  Box,
  Button,
  Chip,
  Container,
  InputAdornment,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SearchIcon from "@material-ui/icons/Search";
import { Pagination } from "@material-ui/lab";
import managerTestApi from "api/managerTest";
import { useConfirm } from "material-ui-confirm";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import clsx from "clsx";

export interface ListUserByExamProps {
  hideHeader?: boolean;
}

export default function ListUserByExam(props: ListUserByExamProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { state } = useLocation();

  const [isSticky, setIsSticky] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [listUsersByExam, setListUsersByExamId] = useState<any[]>([]);
  const [listUserByExamInit, setListUsersByExamInit] = useState<any[]>([]);
  const [submissionMap, setSubmissionMap] = useState<Record<number, any>>({});
  const [searchQuery, setSearchQuery] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const useStyles = makeStyles((theme: Theme) => ({
    container: {
      paddingTop: props?.hideHeader ? 0 : 28,
      paddingBottom: 48,
      position: "relative",
      [theme.breakpoints?.down("sm")]: {
        paddingTop: 12,
        paddingBottom: 16,
        paddingLeft: "0px !important",
        paddingRight: "0px !important",
      },
    },
    backButton: {
      marginBottom: 20,
      color: "#475569",
      fontWeight: 700,
      fontSize: "0.875rem",
      textTransform: "none",
      borderRadius: 10,
      padding: "6px 14px",
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#F1F5F9",
        color: "#0F172A",
        transform: "translateX(-2px)",
      },
    },
    headerBanner: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      borderRadius: 24,
      padding: "32px 36px",
      color: "#FFFFFF",
      marginBottom: 28,
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.25)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 20,
      [theme.breakpoints?.down("sm")]: {
        padding: "20px 18px",
        borderRadius: 16,
      },
    },
    headerGlow: {
      position: "absolute",
      top: "-40%",
      right: "-10%",
      width: 350,
      height: 350,
      background: "radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, rgba(99, 102, 241, 0) 70%)",
      pointerEvents: "none",
    },
    headerBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      backdropFilter: "blur(8px)",
      padding: "4px 14px",
      borderRadius: 20,
      fontSize: "0.75rem",
      fontWeight: 800,
      color: "#818CF8",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: 12,
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },
    headerTitle: {
      fontSize: "1.75rem",
      fontWeight: 900,
      color: "#FFFFFF",
      marginBottom: 12,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "1.25rem",
      },
    },
    headerSubtitle: {
      fontSize: "0.95rem",
      color: "#94A3B8",
      fontWeight: 500,
    },
    filterContainer: {
      position: "sticky",
      top: isMobile ? 68 : 76,
      zIndex: 11,
      backgroundColor: "rgba(255, 255, 255, 0.85)",
      backdropFilter: "blur(12px)",
      borderRadius: 16,
      padding: "16px 20px",
      marginBottom: 24,
      border: "1px solid rgba(226, 232, 240, 0.8)",
      boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
      [theme.breakpoints?.down("sm")]: {
        padding: "12px 14px",
        borderRadius: 14,
        marginBottom: 16,
      },
    },
    filterStickyActive: {
      backgroundColor: "#FFFFFF !important",
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05) !important",
      borderColor: "#CBD5E1 !important",
    },
    searchInput: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#F8FAFC",
        borderRadius: 12,
        fontSize: "0.875rem",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: "#F1F5F9",
        },
        "&.Mui-focused": {
          backgroundColor: "#FFFFFF",
          boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
        },
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E2E8F0",
      },
    },
    tableCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      border: "1px solid #E2E8F0",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.03)",
      overflow: "hidden",
      marginBottom: 20,
    },
    tableHeaderRow: {
      backgroundColor: "#F8FAFC",
      borderBottom: "2px solid #E2E8F0",
    },
    tableHeaderCell: {
      fontWeight: 800,
      color: "#475569",
      textTransform: "uppercase",
      fontSize: "0.75rem",
      letterSpacing: "0.05em",
      padding: "18px 16px",
      borderBottom: "2px solid #E2E8F0",
      top: "0 !important",
    },
    tableRow: {
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: "#F8FAFC",
      },
    },
    tableCell: {
      padding: "16px 16px",
      fontSize: "0.875rem",
      color: "#334155",
      fontWeight: 500,
      borderBottom: "1px solid #F1F5F9",
    },
    studentName: {
      fontWeight: 700,
      color: "#0F172A",
    },
    staffId: {
      fontFamily: "monospace",
      backgroundColor: "#F1F5F9",
      color: "#475569",
      padding: "2px 8px",
      borderRadius: 6,
      fontSize: "0.75rem",
      fontWeight: 700,
    },
    userEmail: {
      color: "#64748B",
      fontSize: "0.85rem",
      fontWeight: 500,
    },
    btnDelete: {
      color: "#EF4444",
      backgroundColor: "#FEF2F2",
      borderRadius: 10,
      padding: "6px 12px",
      fontSize: "0.75rem",
      fontWeight: 700,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#EF4444",
        color: "#FFFFFF",
        boxShadow: "0 4px 12px rgba(239, 68, 68, 0.2)",
      },
    },
    mobileCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 16,
      padding: 16,
      marginBottom: 12,
      border: "1px solid #E2E8F0",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    },
  }));

  const classes = useStyles();

  // Sticky scroll fix per AGENTS.md
  useEffect(() => {
    const targetParents: HTMLElement[] = [];
    const originalOverflows: string[] = [];

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent && parent.tagName !== "MAIN") {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.overflow === "hidden" ||
          computedStyle.overflowX === "hidden" ||
          computedStyle.overflowY === "hidden"
        ) {
          targetParents.push(parent);
          originalOverflows.push(parent.style.overflow);
          parent.style.overflow = "visible";
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      targetParents.forEach((parent, index) => {
        if (parent) {
          parent.style.overflow = originalOverflows[index];
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 180) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getListUserByExamId = useCallback(async (examId: any) => {
    try {
      const [resUsers, resTest]: [any, any] = await Promise.all([
        managerTestApi.getUsersByExamID(examId),
        managerTestApi.getListTest(Number(examId), { page: 0, size: 9999 }).catch(() => null),
      ]);

      const userList = resUsers?.userList || [];
      setListUsersByExamId(userList);
      setListUsersByExamInit(userList);

      if (resTest && resTest.submissionDetails && resTest.submissionDetails.content) {
        const map: Record<number, any> = {};
        resTest.submissionDetails.content.forEach((sub: any) => {
          if (sub?.student?.id) {
            map[sub.student.id] = sub;
          }
        });
        setSubmissionMap(map);
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    if (id) {
      getListUserByExamId(id);
    }
  }, [id, getListUserByExamId]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setSearchQuery(val);
    setPage(1);

    if (!val.trim()) {
      setListUsersByExamId(listUserByExamInit);
    } else {
      const query = val.toLowerCase().trim();
      const filtered = listUserByExamInit.filter(
        (user: any) =>
          (user.name && user.name.toLowerCase().includes(query)) ||
          (user.staffId && user.staffId.toLowerCase().includes(query)) ||
          (user.email && user.email.toLowerCase().includes(query))
      );
      setListUsersByExamId(filtered);
    }
  };

  const handleDeleteUser = async (user: any) => {
    confirm({
      title: (
        <Typography variant="h6" style={{ fontWeight: 800, color: "#0F172A" }}>
          Xác nhận xóa học viên
        </Typography>
      ),
      description: (
        <Typography variant="body2" style={{ color: "#475569" }}>
          Bạn có chắc chắn muốn xóa học viên <strong>{user.name}</strong> ({user.staffId}) khỏi bài thi này?
        </Typography>
      ),
      confirmationText: "Đồng ý",
      cancellationText: "Hủy bỏ",
    }).then(async () => {
      try {
        const value = {
          examID: id,
          userIdList: [user.id],
        };
        const res: any = await managerTestApi.deleteUserFromExam(value);
        if (res.responseCode === "00") {
          toast.success("Xóa học viên khỏi bài thi thành công");
          getListUserByExamId(id);
        }
      } catch (error) {}
    });
  };

  const totalElements = listUsersByExam.length;
  const totalPages = Math.ceil(totalElements / pageSize);
  const paginatedUsers = listUsersByExam.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div ref={containerRef}>
      <Container maxWidth="xl" className={classes.container}>
        {!props?.hideHeader && (
          <>
            <Button
              className={classes.backButton}
              startIcon={<ArrowBackIcon style={{ fontSize: 18 }} />}
              onClick={() => navigate(-1)}
            >
              Quay lại
            </Button>

            <Box className={classes.headerBanner}>
              <div className={classes.headerGlow} />
              <Box zIndex={2}>
                <div className={classes.headerBadge}>Quản lý học viên</div>
                <Typography variant="h4" className={classes.headerTitle}>
                  Học viên dự thi
                </Typography>
                <Typography className={classes.headerSubtitle}>
                  Danh sách học viên được phân công làm bài kiểm tra này
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Sticky Filter Toolbar */}
        <Box className={clsx(classes.filterContainer, isSticky && classes.filterStickyActive)}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={16}>
            <Box flex={1} minWidth={isMobile ? "100%" : 280}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Tìm kiếm theo Tên, Mã NV, Email..."
                value={searchQuery}
                onChange={handleSearchChange}
                className={classes.searchInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#94A3B8" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* List Content */}
        {isMobile ? (
          <Box>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map((user: any, index: number) => {
                const sub = submissionMap[user.id];
                return (
                  <Box key={user.id || index} className={classes.mobileCard}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                      <Box display="flex" flexDirection="column">
                        <Typography className={classes.studentName}>{user.name}</Typography>
                        {user.email && (
                          <Typography className={classes.userEmail} style={{ marginTop: 2 }}>
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                      {sub ? (
                        <Chip
                          label={`${sub.point != null ? sub.point : 0} điểm`}
                          size="small"
                          onClick={() =>
                            navigate(`/admin/manager-test/exam/list-test/detail/${sub.id}`, {
                              state: { examId: sub.examId || id },
                            })
                          }
                          style={{
                            backgroundColor: "#ECFDF5",
                            color: "#047857",
                            fontWeight: 800,
                            borderRadius: 6,
                            fontSize: "0.75rem",
                            cursor: "pointer",
                          }}
                        />
                      ) : (
                        <Chip
                          label="Chưa làm bài"
                          size="small"
                          style={{
                            backgroundColor: "#F1F5F9",
                            color: "#64748B",
                            fontWeight: 600,
                            borderRadius: 6,
                            fontSize: "0.75rem",
                          }}
                        />
                      )}
                    </Box>

                    <Box display="flex" alignItems="center" justifyContent="space-between" pt={1.5} borderTop="1px solid #F1F5F9">
                      <span className={classes.staffId}>{user.staffId || "N/A"}</span>
                      <Button
                        disabled={Boolean(state?.disabled)}
                        className={classes.btnDelete}
                        size="small"
                        onClick={() => handleDeleteUser(user)}
                      >
                        <DeleteOutlineIcon style={{ fontSize: 16 }} /> Xóa
                      </Button>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Paper className={classes.tableCard} style={{ padding: 32, textAlign: "center" }}>
                <Typography color="textSecondary" style={{ fontWeight: 600 }}>
                  Không tìm thấy học viên dự thi
                </Typography>
              </Paper>
            )}
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableCard} elevation={0}>
            <Table stickyHeader>
              <TableHead>
                <TableRow className={classes.tableHeaderRow}>
                  <TableCell align="center" className={classes.tableHeaderCell} style={{ width: 80 }}>
                    STT
                  </TableCell>
                  <TableCell align="left" className={classes.tableHeaderCell} style={{ minWidth: 260 }}>
                    Thí sinh
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeaderCell} style={{ width: 160 }}>
                    Mã nhân viên
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeaderCell} style={{ width: 160 }}>
                    Kết quả thi
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeaderCell} style={{ width: 160 }}>
                    Thao tác
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.length > 0 ? (
                  paginatedUsers.map((user: any, index: number) => {
                    const sub = submissionMap[user.id];
                    return (
                      <TableRow key={user.id || index} className={classes.tableRow}>
                        <TableCell align="center" className={classes.tableCell}>
                          {(page - 1) * pageSize + index + 1}
                        </TableCell>
                        <TableCell align="left" className={classes.tableCell}>
                          <Box display="flex" flexDirection="column">
                            <span className={classes.studentName}>{user.name}</span>
                            {user.email && (
                              <span className={classes.userEmail} style={{ marginTop: 2 }}>
                                {user.email}
                              </span>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center" className={classes.tableCell}>
                          <span className={classes.staffId}>{user.staffId || "N/A"}</span>
                        </TableCell>
                        <TableCell align="center" className={classes.tableCell}>
                          {sub ? (
                            <Tooltip title="Bấm để xem chi tiết bài làm">
                              <Chip
                                label={`${sub.point != null ? sub.point : 0} điểm`}
                                size="small"
                                onClick={() =>
                                  navigate(`/admin/manager-test/exam/list-test/detail/${sub.id}`, {
                                    state: { examId: sub.examId || id },
                                  })
                                }
                                style={{
                                  backgroundColor: "#ECFDF5",
                                  color: "#047857",
                                  fontWeight: 800,
                                  borderRadius: 6,
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              label="Chưa làm bài"
                              size="small"
                              style={{
                                backgroundColor: "#F1F5F9",
                                color: "#64748B",
                                fontWeight: 600,
                                borderRadius: 6,
                                fontSize: "0.75rem",
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center" className={classes.tableCell}>
                          <Button
                            disabled={Boolean(state?.disabled)}
                            className={classes.btnDelete}
                            onClick={() => handleDeleteUser(user)}
                          >
                            <DeleteOutlineIcon style={{ fontSize: 16 }} /> Xóa
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" style={{ padding: 40 }}>
                      <Typography color="textSecondary" style={{ fontWeight: 600 }}>
                        Không có dữ liệu học viên dự thi
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination per AGENTS.md */}
        {totalPages > 0 && (
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems="center"
            gridGap={16}
            mt={2}
            px={1}
          >
            <Typography variant="body2" style={{ color: "#64748B", fontWeight: 600 }}>
              Hiển thị {paginatedUsers.length} trên tổng số {totalElements} học viên được phân công
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
              shape="rounded"
              size={isMobile ? "small" : "medium"}
              siblingCount={isMobile ? 0 : 1}
            />
          </Box>
        )}
      </Container>
    </div>
  );
}
